import React, { useState, useMemo } from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
  Icon,
  Spinner,
  Center,
  Box,
} from '@chakra-ui/react';
import { MdEdit } from 'react-icons/md';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

type Categoria = {
  id: number;
  nomeAplicativo: string;
  ativo: boolean;
};

interface TabelaAplicativoProps {
  listaAplicativo: Categoria[];
  listaVazia: string;
  loading: boolean;
}

type Ordenacao = { chave: keyof Categoria; direcao: 'asc' | 'desc' } | null;

const TabelaAplicativo = ({ listaAplicativo, listaVazia, loading }: TabelaAplicativoProps) => {
  const navigate = useNavigate();
  const [ordenacao, setOrdenacao] = useState<Ordenacao>(null);
  const [colunaDestacada, setColunaDestacada] = useState<keyof Categoria | null>(null);

  const manipularOrdenacao = (chave: keyof Categoria) => {
    let direcao: 'asc' | 'desc' = 'asc';
    if (ordenacao && ordenacao.chave === chave && ordenacao.direcao === 'asc') {
      direcao = 'desc';
    }
    setOrdenacao({ chave, direcao });
  };

  const listaOrdenada = useMemo(() => {
    const copia = [...listaAplicativo];
    if (ordenacao !== null) {
      copia.sort((a, b) => {
        const valorA = a[ordenacao.chave];
        const valorB = b[ordenacao.chave];
        if (typeof valorA === 'string' && typeof valorB === 'string') {
          const comparacao = valorA.localeCompare(valorB);
          return ordenacao.direcao === 'asc' ? comparacao : -comparacao;
        }
        if (valorA < valorB) return ordenacao.direcao === 'asc' ? -1 : 1;
        if (valorA > valorB) return ordenacao.direcao === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return copia;
  }, [listaAplicativo, ordenacao]);

  const obterIndicadorOrdenacao = (chave: keyof Categoria) => {
    if (ordenacao && ordenacao.chave === chave) {
      return ordenacao.direcao === 'asc' ? (
        <FaCaretUp color="#1789FC" style={{ marginLeft: '4px' }} />
      ) : (
        <FaCaretDown color="#1789FC" style={{ marginLeft: '4px' }} />
      );
    }
      return <FaCaretUp color="#4a5568" style={{ marginLeft: '4px' }} />;
  };

  const renderRows = () =>
    listaOrdenada.length > 0 ? (
      listaOrdenada.map((aplicativo) => (
        <Tr key={aplicativo.id}>
          <Td>{aplicativo.id}</Td>
          <Td isTruncated maxW="200px">{aplicativo.nomeAplicativo}</Td>
          <Td>{aplicativo.ativo ? 'Ativo' : 'Inativo'}</Td>
          <Td>
            <IconButton
              aria-label="Botão Editar"
              bgColor="#fff"
              onClick={() => navigate(`editar-aplicativo/${aplicativo.id}`)}
            >
              <Icon as={MdEdit} fontSize="24px" color="#1789FC" />
            </IconButton>
          </Td>
        </Tr>
      ))
    ) : (
      <Tr>
        <Td colSpan={5} color="#999898" textAlign="center">
          {listaVazia}
        </Td>
      </Tr>
    );

  return (
    <TableContainer>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th
              onClick={() => manipularOrdenacao('id')}
              onMouseEnter={() => setColunaDestacada('id')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
              _hover={{ transition: '0.1s', color: 'blue.500' }}
              color={ordenacao?.chave === 'id' || colunaDestacada === 'id' ? 'blue.500' : '#4a5568'}
            >
              <Box display="flex" alignItems="center">
                ID {obterIndicadorOrdenacao('id')}
              </Box>
            </Th>
            <Th
              onClick={() => manipularOrdenacao('nomeAplicativo')}
              onMouseEnter={() => setColunaDestacada('nomeAplicativo')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
              _hover={{ transition: '0.1s', color: 'blue.500' }}
              color={ordenacao?.chave === 'nomeAplicativo' || colunaDestacada === 'nomeAplicativo' ? 'blue.500' : '#4a5568'}
            >
              <Box display="flex" alignItems="center">
                Nome do Aplicativo {obterIndicadorOrdenacao('nomeAplicativo')}
              </Box>
            </Th>
            <Th
              onClick={() => manipularOrdenacao('ativo')}
              onMouseEnter={() => setColunaDestacada('ativo')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
              _hover={{ transition: '0.1s', color: 'blue.500' }}
              color={ordenacao?.chave === 'ativo' || colunaDestacada === 'ativo' ? 'blue.500' : '#4a5568'}
            >
              <Box display="flex" alignItems="center">
                Ativo {obterIndicadorOrdenacao('ativo')}
              </Box>
            </Th>
            <Th>Editar</Th>
          </Tr>
        </Thead>
        <Tbody>
          {loading ? (
            <Tr>
              <Td colSpan={4}>
                <Center>
                  <Spinner size="lg" color="blue.500" />
                </Center>
              </Td>
            </Tr>
          ) : (
            renderRows()
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default TabelaAplicativo;
